import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Layers,
  Database,
  BarChart3,
  Headphones,
  RefreshCw,
  CheckCircle2,
  Quote,
  Boxes,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1760246964044-1384f71665b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmUlMjBidWlsZGluZ3xlbnwwfHx8fDE3NzczMDIyMzl8MA&ixlib=rb-4.1.0&q=85";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1774186184398-1cc2da3d029e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBleGVjdXRpdmUlMjBtZWV0aW5nJTIwb2ZmaWNlfGVufDB8fHx8MTc3NzMwMjIzOXww&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  {
    icon: Layers,
    title: "SAP Implementation & Migration",
    description:
      "We guide your adoption of SAP — including SAP S/4HANA — using proven roadmaps with minimal disruption, and migrate legacy systems to cloud-ready environments.",
    outcome: "Smarter processes powered by real-time analytics and AI insights.",
  },
  {
    icon: Database,
    title: "SAP S/4HANA Enablement",
    description:
      "Enable next-gen ERP with S/4HANA. From system configuration to custom Fiori apps, we optimise data models, automate workflows, and deliver in-memory performance.",
    outcome: "Rapid decisions and uncompromised business agility.",
  },
  {
    icon: BarChart3,
    title: "Business Analytics & Reporting",
    description:
      "We integrate your data into interactive dashboards and predictive reports — empowering teams with clear visibility into trends, performance, and operations.",
    outcome: "Faster, smarter, evidence-led decisions.",
  },
  {
    icon: Headphones,
    title: "Application Management Services",
    description:
      "Keep your SAP systems performing at peak with round-the-clock support — incident resolution, updates, change requests, and ongoing user capability building.",
    outcome: "Stable systems so your team can focus on growth.",
  },
  {
    icon: Boxes,
    title: "Product Development",
    description:
      "Custom product engineering across SAP and non-SAP stacks — from solution architecture and Fiori-based front-ends to API integrations and bespoke modules built around your operating model.",
    outcome: "Tailored products that extend SAP and accelerate your roadmap.",
    wide: true,
  },
];

const VALUE_POINTS = [
  "Deep SAP Expertise — certified consultants with broad cross-industry experience",
  "Client-First Approach — solutions tailored to your vision, timeline, and objectives",
  "Proven Methodologies — structured best practices from discovery to deployment and beyond",
  "Smart & Scalable — AI-enhanced, future-proof solutions that evolve with your business",
  "End-to-End Service — advisory, deployment, upgrades, and long-term support in one trusted partner",
];

const STATS = [
  { value: "SAP Business Suite", label: "HANA · ECC · B1 · ByD · Public & Private Cloud" },
  { value: "5+", label: "Years of practice" },
  { value: "AI / ML", label: "Product development & data integration specialisation" },
  { value: "24/7", label: "Application support" },
];

const PROCESS = [
  {
    step: "01",
    title: "Discover",
    description:
      "We immerse with your stakeholders, audit your existing IT and SAP landscape, and surface the gaps that are quietly limiting performance.",
  },
  {
    step: "02",
    title: "Deploy",
    description:
      "We architect the right SAP solution — implementation, migration, or upgrade — sequenced to minimise disruption and maximise time-to-value.",
  },
  {
    step: "03",
    title: "Sustain",
    description:
      "We stay on as your AMS partner — monitoring, optimising, building your team's capability, and rolling out enhancements as your business evolves.",
  },
];

const TESTIMONIALS = [
  {
    quote:
      "Anila Consulting delivered our S/4HANA migration on time and well under what we'd budgeted. The team's depth on FICO and MM modules was the difference.",
    name: "Operations Director",
    role: "Manufacturing · India",
  },
  {
    quote:
      "Their AMS support has been transformative. Tickets that used to take days now resolve in hours, and they've actively suggested optimisations we hadn't considered.",
    name: "Head of IT",
    role: "FMCG · India",
  },
];

const INDUSTRIES = [
  "Retail",
  "Manufacturing",
  "Services",
  "FMCG & Oil",
  "Real Estate",
  "Infrastructure",
];

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[90vh] flex items-center"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Modern corporate architecture"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        <div className="relative container-pro py-32 max-w-3xl">
          <p className="overline mb-6 fade-up">SAP &amp; IT Business Consulting</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-[#111111] fade-up-delay-1">
            We create a culture
            <br />
            that inspires us to
            <br />
            <span className="text-[#8B0000]">work smart, together.</span>
          </h1>
          <p className="mt-6 font-display italic text-2xl lg:text-3xl text-[#8B0000] fade-up-delay-2">
            Empowering Business. Accelerating Growth.
          </p>
          <p className="mt-8 text-lg text-[#4A4A4A] max-w-2xl leading-relaxed fade-up-delay-2">
            True partners for your end-to-end IT needs. Anila Consulting helps
            ambitious organisations achieve their business objectives through
            SAP &amp; IT initiatives — at the lowest possible cost.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4 fade-up-delay-3">
            <Link
              to="/services"
              data-testid="hero-secondary-cta"
              className="btn-primary"
            >
              Explore Our Services <ArrowRight size={18} />
            </Link>
          </div>

          <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8 max-w-3xl fade-up-delay-3">
            {STATS.map((s) => (
              <div
                key={s.label}
                data-testid={`hero-stat-${s.label}`}
                className="border-l-2 border-[#8B0000] pl-4"
              >
                <p className="font-display text-2xl lg:text-[26px] text-[#8B0000] leading-tight">
                  {s.value}
                </p>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#4A4A4A] mt-2 leading-relaxed">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT INTRO — beige */}
      <section
        data-testid="about-intro-section"
        className="section bg-[#F5F5DC]"
      >
        <div className="container-pro grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <img
              src={ABOUT_IMG}
              alt="Business consultation"
              className="w-full h-[480px] object-cover"
            />
          </div>
          <div className="lg:col-span-7 lg:pl-8">
            <p className="overline mb-5">Who we are</p>
            <h2 className="font-display text-4xl lg:text-5xl text-[#111111] leading-tight">
              An IT consulting firm built on
              <span className="text-[#8B0000]"> partnership, not vendorship.</span>
            </h2>
            <span className="divider-red mt-6 mb-6" />
            <p className="text-[#4A4A4A] text-lg leading-relaxed">
              Founded in 2024, Anila Consulting is an IT business consulting
              firm engaged in a diverse portfolio of SAP and analytics
              services. We work as partners — with ownership — instead of
              suppliers, and we genuinely understand the pain areas of the
              organisations we serve.
            </p>
            <p className="text-[#4A4A4A] mt-5 leading-relaxed">
              Our commitment is simple: deliver high-quality services in the
              niche industry verticals of SAP HANA/ECC, SAP B1, SAP ByD, Big
              Data Analytics, and Automation — at the lowest possible cost.
            </p>
            <Link
              to="/about"
              data-testid="about-intro-link"
              className="inline-flex items-center gap-2 mt-8 text-[#8B0000] font-medium uppercase tracking-wider text-sm hover:gap-4 transition-all"
            >
              More about our firm <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW — white */}
      <section
        data-testid="services-overview-section"
        className="section bg-white"
      >
        <div className="container-pro">
          <div className="max-w-3xl mb-16">
            <p className="overline mb-5">What we do</p>
            <h2 className="font-display text-4xl lg:text-5xl text-[#111111] leading-tight">
              SAP, analytics, and managed services —
              <br />
              <span className="text-[#8B0000]">delivered end-to-end.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
            {SERVICES.map((service) => {
              const Icon = service.icon;
              return (
                <article
                  key={service.title}
                  data-testid={`service-card-${service.title}`}
                  className={`bg-white p-10 lg:p-12 group hover:bg-[#FAF7EE] transition-colors duration-300 ${
                    service.wide ? "md:col-span-2" : ""
                  }`}
                >
                  <Icon
                    size={32}
                    strokeWidth={1.5}
                    className="text-[#8B0000] mb-8"
                  />
                  <h3 className="font-display text-3xl text-[#111111] mb-4">
                    {service.title}
                  </h3>
                  <p className="text-[#4A4A4A] leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <p className="text-sm font-medium text-[#111111] border-t border-[#E5E5E5] pt-5">
                    <span className="text-[#8B0000] uppercase tracking-[0.2em] text-xs mr-3">
                      Outcome
                    </span>
                    {service.outcome}
                  </p>
                </article>
              );
            })}
          </div>

          <div className="mt-12">
            <Link
              to="/services"
              data-testid="services-overview-link"
              className="btn-secondary"
            >
              View all services <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US — dark charcoal */}
      <section
        data-testid="why-choose-us-section"
        className="section bg-[#111111] text-white"
      >
        <div className="container-pro grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline mb-5">Why Anila Consulting</p>
            <h2 className="font-display text-4xl lg:text-5xl text-white leading-tight">
              Five reasons leaders choose
              <br />
              <span className="text-[#8B0000]">our SAP consultancy.</span>
            </h2>
            <p className="text-white/70 mt-6 leading-relaxed">
              We don't disappear after go-live. We stay on as a long-term
              partner to your business, with deep SAP expertise and a
              client-first approach that's earned us repeat engagements
              across industries.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="space-y-6">
              {VALUE_POINTS.map((point, i) => (
                <li
                  key={point}
                  data-testid={`value-point-${i}`}
                  className="flex items-start gap-5 pb-6 border-b border-white/15"
                >
                  <CheckCircle2
                    size={22}
                    strokeWidth={1.5}
                    className="text-[#8B0000] flex-shrink-0 mt-1"
                  />
                  <p className="text-lg text-white/90 font-light leading-relaxed">
                    {point}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PROCESS — beige */}
      <section
        data-testid="process-section"
        className="section bg-[#F5F5DC]"
      >
        <div className="container-pro">
          <div className="max-w-3xl mb-16">
            <p className="overline mb-5">Our methodology</p>
            <h2 className="font-display text-4xl lg:text-5xl text-[#111111] leading-tight">
              Discover. Deploy. Sustain.
              <br />
              <span className="text-[#8B0000]">A proven SAP delivery flow.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
            {PROCESS.map((p, i) => (
              <div
                key={p.step}
                data-testid={`process-step-${p.step}`}
                className="bg-white p-10 border-t-2 border-[#8B0000] hover:shadow-xl transition-shadow duration-300 relative"
              >
                <p className="font-display text-7xl text-[#F5F5DC] absolute right-6 top-4 select-none">
                  {p.step}
                </p>
                <p className="overline mb-4 relative">{`Phase ${i + 1}`}</p>
                <h3 className="font-display text-3xl text-[#111111] mb-4 relative">
                  {p.title}
                </h3>
                <p className="text-[#4A4A4A] leading-relaxed relative">
                  {p.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES — white */}
      <section data-testid="industries-section" className="section bg-white">
        <div className="container-pro">
          <div className="max-w-3xl mb-12">
            <p className="overline mb-5">Industries we serve</p>
            <h2 className="font-display text-4xl lg:text-5xl text-[#111111] leading-tight">
              Recognised as a solution provider
              <br />
              <span className="text-[#8B0000]">across six core verticals.</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
            {INDUSTRIES.map((ind) => (
              <div
                key={ind}
                data-testid={`industry-${ind}`}
                className="bg-white p-8 text-center hover:bg-[#F5F5DC] transition-colors duration-300"
              >
                <p className="font-display text-xl text-[#111111]">{ind}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — beige */}
      <section
        data-testid="testimonials-section"
        className="section bg-[#F5F5DC]"
      >
        <div className="container-pro">
          <div className="max-w-3xl mb-16">
            <p className="overline mb-5">Trusted by clients</p>
            <h2 className="font-display text-4xl lg:text-5xl text-[#111111] leading-tight">
              Words from teams who've
              <span className="text-[#8B0000]"> gone live with us.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
            {TESTIMONIALS.map((t) => (
              <figure
                key={t.name}
                data-testid={`testimonial-${t.name}`}
                className="bg-white p-10 lg:p-14 flex flex-col justify-between"
              >
                <Quote
                  size={36}
                  strokeWidth={1.5}
                  className="text-[#8B0000] mb-6"
                />
                <blockquote className="font-display text-2xl lg:text-3xl text-[#111111] leading-snug">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-10 pt-6 border-t border-[#E5E5E5]">
                  <p className="font-medium text-[#111111]">{t.name}</p>
                  <p className="text-sm text-[#4A4A4A]">{t.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA — deep red banner */}
      <section
        data-testid="final-cta-section"
        className="bg-[#8B0000] text-white py-24 lg:py-32"
      >
        <div className="container-pro grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <p className="overline mb-5" style={{ color: "rgba(255,255,255,0.85)" }}>
              Begin the conversation
            </p>
            <h2 className="font-display text-4xl lg:text-6xl leading-tight text-white">
              Ready to transform
              <br /> your SAP landscape?
            </h2>
            <p className="text-white/80 mt-6 text-lg max-w-2xl leading-relaxed">
              Tell us about your environment and objectives. A senior SAP
              consultant will respond personally — no scripts, no junior
              hand-offs.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link
              to="/contact"
              data-testid="final-cta-button"
              className="btn-ghost-light"
            >
              Get a Quote <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
